/**
 * Equation Balancer using RDKit.js
 * 
 * Balances chemical equations by:
 * 1. Parsing reactants and products
 * 2. Using RDKit to get molecular formulas
 * 3. Setting up element conservation equations
 * 4. Solving the linear system for stoichiometric coefficients
 */

import { loadRDKit } from './rdkitLoader';

export interface BalancedEquation {
  reactants: Array<{ formula: string; coefficient: number }>;
  products: Array<{ formula: string; coefficient: number }>;
  balanced: string;
  error?: string;
}

/**
 * Parse molecular formula to element counts
 * Example: "H2O" -> { H: 2, O: 1 }
 */
function parseFormula(formula: string): Record<string, number> {
  const counts: Record<string, number> = {};
  const regex = /([A-Z][a-z]?)(\d*)/g;
  let match;

  while ((match = regex.exec(formula)) !== null) {
    const element = match[1];
    const count = match[2] ? parseInt(match[2], 10) : 1;
    counts[element] = (counts[element] || 0) + count;
  }

  return counts;
}

/**
 * Get molecular formula from SMILES using RDKit
 */
async function getFormulaFromSMILES(smiles: string, RDKit: any): Promise<string> {
  try {
    const mol = RDKit.get_mol(smiles);
    if (!mol) {
      throw new Error(`Failed to parse SMILES: ${smiles}`);
    }

    const inchi = mol.get_inchi();
    if (!inchi) {
      mol.delete();
      throw new Error(`Failed to get InChI for: ${smiles}`);
    }

    // Extract formula from InChI string
    // InChI format: InChI=1S/C2H6O/c1-2-3/h3H,2H2,1H3
    const inchiParts = inchi.split('/');
    if (inchiParts.length > 1) {
      const formulaPart = inchiParts[1];
      const formulaMatch = formulaPart.match(/^([A-Z][a-z]?\d*)+/);
      if (formulaMatch) {
        mol.delete();
        return formulaMatch[0];
      }
    }

    mol.delete();
    throw new Error(`Could not extract formula from InChI: ${inchi}`);
  } catch (err: any) {
    throw new Error(`Error processing SMILES "${smiles}": ${err.message}`);
  }
}

/**
 * Parse chemical equation into reactants and products
 */
function parseEquation(equation: string): { reactants: string[]; products: string[] } {
  // Support both -> and = as separators
  const separator = equation.includes('->') ? '->' : equation.includes('=') ? '=' : null;
  if (!separator) {
    throw new Error('Invalid equation format. Use "->" or "=" to separate reactants and products.');
  }

  const parts = equation.split(separator).map((p) => p.trim());
  if (parts.length !== 2) {
    throw new Error('Invalid equation format. Expected: reactants -> products');
  }

  const reactants = parts[0].split('+').map((r) => r.trim()).filter(Boolean);
  const products = parts[1].split('+').map((p) => p.trim()).filter(Boolean);

  if (reactants.length === 0 || products.length === 0) {
    throw new Error('Equation must have at least one reactant and one product.');
  }

  return { reactants, products };
}

/**
 * Get all unique elements from a set of formulas
 */
function getAllElements(formulas: string[]): string[] {
  const elements = new Set<string>();
  formulas.forEach((formula) => {
    const counts = parseFormula(formula);
    Object.keys(counts).forEach((el) => elements.add(el));
  });
  return Array.from(elements).sort();
}

/**
 * Build matrix for element conservation
 * Returns [A, b] where Ax = b, x is coefficients
 */
function buildMatrix(
  reactantFormulas: string[],
  productFormulas: string[],
  elements: string[]
): { matrix: number[][]; rhs: number[] } {
  const numReactants = reactantFormulas.length;
  const numProducts = productFormulas.length;
  const numCoeffs = numReactants + numProducts;
  const numEquations = elements.length;

  // Initialize matrix and RHS
  const matrix: number[][] = [];
  const rhs: number[] = new Array(numEquations).fill(0);

  // For each element, create an equation: sum(reactant_coeffs * counts) = sum(product_coeffs * counts)
  elements.forEach((element, eqIdx) => {
    const row: number[] = new Array(numCoeffs).fill(0);

    // Reactants (positive coefficients)
    reactantFormulas.forEach((formula, idx) => {
      const counts = parseFormula(formula);
      row[idx] = counts[element] || 0;
    });

    // Products (negative coefficients, moved to RHS)
    productFormulas.forEach((formula, idx) => {
      const counts = parseFormula(formula);
      row[numReactants + idx] = -(counts[element] || 0);
    });

    matrix.push(row);
  });

  return { matrix, rhs };
}

/**
 * Solve linear system using Gaussian elimination with back substitution
 * Returns coefficients normalized to smallest integers
 */
function solveLinearSystem(matrix: number[][], rhs: number[]): number[] {
  const n = matrix.length;
  const m = matrix[0].length;

  // Augmented matrix [A | b]
  const augmented: number[][] = matrix.map((row, i) => [...row, rhs[i]]);

  // Gaussian elimination
  for (let i = 0; i < Math.min(n, m); i++) {
    // Find pivot
    let pivotRow = i;
    for (let k = i + 1; k < n; k++) {
      if (Math.abs(augmented[k][i]) > Math.abs(augmented[pivotRow][i])) {
        pivotRow = k;
      }
    }

    // Swap rows
    [augmented[i], augmented[pivotRow]] = [augmented[pivotRow], augmented[i]];

    // Skip if pivot is zero
    if (Math.abs(augmented[i][i]) < 1e-10) {
      continue;
    }

    // Eliminate
    for (let k = i + 1; k < n; k++) {
      const factor = augmented[k][i] / augmented[i][i];
      for (let j = i; j <= m; j++) {
        augmented[k][j] -= factor * augmented[i][j];
      }
    }
  }

  // Back substitution
  const solution: number[] = new Array(m).fill(1); // Initialize with 1s
  for (let i = Math.min(n, m) - 1; i >= 0; i--) {
    let sum = augmented[i][m];
    for (let j = i + 1; j < m; j++) {
      sum -= augmented[i][j] * solution[j];
    }
    if (Math.abs(augmented[i][i]) > 1e-10) {
      solution[i] = sum / augmented[i][i];
    }
  }

  // Normalize to smallest integers
  return normalizeCoefficients(solution);
}

/**
 * Normalize coefficients to smallest integers
 */
function normalizeCoefficients(coeffs: number[]): number[] {
  // Find GCD of all coefficients
  const gcd = (a: number, b: number): number => {
    if (Math.abs(b) < 1e-10) return Math.abs(a);
    return gcd(b, a % b);
  };

  // Convert to integers (multiply by large number, round, then normalize)
  const scaled = coeffs.map((c) => Math.round(c * 10000));
  let commonDivisor = Math.abs(scaled[0]);
  for (let i = 1; i < scaled.length; i++) {
    commonDivisor = gcd(commonDivisor, Math.abs(scaled[i]));
  }

  if (commonDivisor === 0) {
    return coeffs.map(() => 1);
  }

  const normalized = scaled.map((c) => c / commonDivisor);
  
  // Ensure all are positive integers
  const minCoeff = Math.min(...normalized.filter((c) => c > 0));
  const factor = 1 / minCoeff;
  
  return normalized.map((c) => Math.round(c * factor));
}

/**
 * Format balanced equation as string
 */
function formatBalancedEquation(
  reactants: string[],
  products: string[],
  coeffs: number[]
): string {
  const numReactants = reactants.length;
  const reactantStr = reactants
    .map((r, i) => {
      const coeff = coeffs[i];
      return coeff === 1 ? r : `${coeff} ${r}`;
    })
    .join(' + ');

  const productStr = products
    .map((p, i) => {
      const coeff = coeffs[numReactants + i];
      return coeff === 1 ? p : `${coeff} ${p}`;
    })
    .join(' + ');

  return `${reactantStr} → ${productStr}`;
}

/**
 * Balance chemical equation using RDKit
 */
export async function balanceEquation(equation: string): Promise<BalancedEquation> {
  try {
    // Parse equation
    const { reactants, products } = parseEquation(equation);

    // Load RDKit
    const RDKit = await loadRDKit();

    // Get formulas for all species (try SMILES first, then assume it's already a formula)
    const reactantFormulas: string[] = [];
    const productFormulas: string[] = [];

    for (const reactant of reactants) {
      try {
        // Try as SMILES first
        const formula = await getFormulaFromSMILES(reactant, RDKit);
        reactantFormulas.push(formula);
      } catch {
        // If SMILES fails, assume it's already a formula
        // Validate by trying to parse it
        parseFormula(reactant);
        reactantFormulas.push(reactant);
      }
    }

    for (const product of products) {
      try {
        const formula = await getFormulaFromSMILES(product, RDKit);
        productFormulas.push(formula);
      } catch {
        parseFormula(product);
        productFormulas.push(product);
      }
    }

    // Get all unique elements
    const allFormulas = [...reactantFormulas, ...productFormulas];
    const elements = getAllElements(allFormulas);

    if (elements.length === 0) {
      throw new Error('No elements found in equation');
    }

    // Build matrix
    const { matrix, rhs } = buildMatrix(reactantFormulas, productFormulas, elements);

    // Solve for coefficients
    const coeffs = solveLinearSystem(matrix, rhs);

    // Format result
    const balanced = formatBalancedEquation(reactants, products, coeffs);

    return {
      reactants: reactants.map((r, i) => ({
        formula: reactantFormulas[i],
        coefficient: coeffs[i],
      })),
      products: products.map((p, i) => ({
        formula: productFormulas[i],
        coefficient: coeffs[reactants.length + i],
      })),
      balanced,
    };
  } catch (err: any) {
    return {
      reactants: [],
      products: [],
      balanced: '',
      error: err.message || 'Failed to balance equation',
    };
  }
}

